import {Component, Inject, OnInit} from '@angular/core';
import {MatDialog, MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {OrganizationService} from "../../../../../service-data/organization.service";
import {Organization} from "../../../../../../model/Organization";
import {ProjectFolders} from "../../../../../../model/supporting/ProjectFolders";
import {Util} from "../../../../../util/Util";
import {ProjectService} from "../../../../../service-data/project.service";
import {debounceTime} from "rxjs/operators";
import {LanguagePipe} from "../../../../../pipe/language.pipe";

@Component({
  selector: 'app-create-project-folder',
  templateUrl: './create-project-folder.component.html',
  styleUrls: ['./create-project-folder.component.css']
})
export class CreateProjectFolderComponent implements OnInit {
  public breakpoint: number; // Breakpoint observer code
  public newFolderName: string;
  public changeForm: FormGroup;
  wasFormChanged = false;
  organization: Organization;
  selectedFolder: string;
  projectFolders: ProjectFolders;
  validFolderName: boolean = false;
  /** Set when ProjectFolders refuses the operation; keeps the dialog open. */
  errorMessage: string = '';

  constructor(private formBuilder: FormBuilder, public dialog: MatDialog, public organizationService: OrganizationService, private projectService: ProjectService,  @Inject(MAT_DIALOG_DATA) data, private dialogRef: MatDialogRef<CreateProjectFolderComponent>) {
    // Get data passed in
    if ( data.organization ) {
      this.organization = data.organization;
      this.projectFolders = this.organization.getProjectFolders();
      this.selectedFolder = data.selectedFolder;
      this.newFolderName = '';
    }
  }

  public ngOnInit(): void {
    const dirNameRegex = /^(?!(?:CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])(?:\.[^.]*)?$)[^<>:"/\\|?*\x00-\x1F]*[^<>:"/\\|?*\x00-\x1F .]$/;
    this.changeForm = this.formBuilder.group({
      newFolderName: [this.newFolderName, [Validators.required, Validators.pattern(dirNameRegex)]]
    });
    this.breakpoint = window.innerWidth <= 600 ? 1 : 2; // Breakpoint observer code

    this.changeForm.get('newFolderName').valueChanges.pipe(debounceTime(300)) // Wait 300ms after the last keystroke
      .subscribe(() => this.update());
  }

  public onChange(): void {
    this.errorMessage = '';
    if ( this.projectFolders ) {
      try {
        this.projectFolders.addFolder(this.selectedFolder, this.changeForm.value['newFolderName']);
        this.organization.setProjectFolders(this.projectFolders);
        this.organizationService.updateItem(this.organization).subscribe( x => {
          console.log(`Create Folder completed ${this.selectedFolder}`);
        });
      } catch ( e ) {
        // ProjectFolders refuses rather than overwrites (it used to assign {}
        // over an existing folder, discarding its whole subtree). Keep the
        // dialog open and say so -- closing on a failure told the user the
        // folder had been created when it had not.
        console.log(`Create Folder not completed ${this.selectedFolder}`, e);
        this.errorMessage = LanguagePipe.get('A folder with that name already exists here.', 'compressSpaces');
        return;
      }
    }
    const returnObject = {};
    // returnObject['selectedFilterValue'] = this.form.controls.selectedFilterValue.value;
    // returnObject['selectedFilter'] = this.form.controls.selectedFilter.value;

    this.markAsDirty(this.changeForm);
    this.dialogRef.close(returnObject);
  }

  public update(): void {
    this.newFolderName = this.changeForm.value['newFolderName'];
    this.validFolderName = this.isValidFolderName(this.newFolderName);
    this.markAsDirty(this.changeForm);
  }

  /**
   * The folder name as it should read on the action button.
   *
   * Read straight off the control instead of the `newFolderName` mirror.
   * The mirror is only refreshed by {@link update}, which hangs off
   * `(input)`/`(blur)`/`(change)` plus a 300ms-debounced `valueChanges` —
   * so any value change that does not go through one of those (a
   * programmatic `setValue`, a browser autofill, or simply reading the
   * button inside the debounce window) left the button advertising a
   * name that was not the one about to be created. The button is the
   * confirmation step; it has to show what will actually happen.
   */
  get folderNameLabel(): string {
    const control = this.changeForm ? this.changeForm.get('newFolderName') : null;
    const value = control ? control.value : this.newFolderName;
    return value ? String(value) : '';
  }

  private isValidFolderName(folderName: string): boolean {
    if ( !folderName ) {
      return false;
    }
    const currentChildrenFolders = this.projectFolders.getChildFolders(this.selectedFolder);
    const possibleNewFolder = this.selectedFolder === '/' ? this.selectedFolder + folderName : this.selectedFolder + '/' + folderName;
    for ( const childFolder of currentChildrenFolders ) {
      if ( childFolder === possibleNewFolder ) {
        return false;
      }
    }
    return true;
  }

  // tslint:disable-next-line:no-any
  public onResize(event: any): void {
    this.breakpoint = event.target.innerWidth <= 600 ? 1 : 2;
  }

  private markAsDirty(group: FormGroup): void {
    group.markAsDirty();
    // tslint:disable-next-line:forin
    for (const i in group.controls) {
      group.controls[i].markAsDirty();
    }
  }

  formChanged() {
    this.wasFormChanged = true;
  }

  handleSelection(name: string) {
    this.newFolderName = name;
    console.log('User selected:', name);
  }
}
